import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Users, Zap, Award, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';

const SpecialGrowthMastery = () => {
  const modules = [
    {
      title: "Growth Psychology for Insurance Professionals",
      desc: "Understand the science behind behaviour, consistency and influence. Train your mind to operate at peak performance even in high-pressure selling environments."
    },
    {
      title: "Recognise Your Biggest Roadblock — and Eliminate It Fast",
      desc: "Identify the hidden mental, emotional or behavioural blocks that stop your success. Learn rapid tools to remove them instantly and create unstoppable momentum."
    },
    {
      title: "Skills That Make You Extraordinary",
      desc: "Master the powerful skills that top performers use — communication, influence psychology, trust building, and strategic selling."
    },
    {
      title: "Digital Innovator Mastery",
      desc: "Use modern digital tools to grow your personal brand, create awareness, generate leads and stay ahead in a rapidly changing insurance industry."
    },
    {
      title: "Personal Branding & Your Magical Success Formula",
      desc: "Design a unique identity that makes you memorable and trustworthy. Create your customised “Success Formula” that guides your daily actions."
    },
    {
      title: "Neuro-Communication for Client Influence",
      desc: "Use language patterns, emotional triggers and trust-building frameworks based on neuroscience to influence clients ethically and effectively."
    },
    {
      title: "High-Performance Habit System",
      desc: "A daily routine designed for insurance success — combining prospecting, follow-up mastery, planning, closing and mindset reinforcement."
    },
    {
      title: "Breakthrough Goal Mastery",
      desc: "Learn how top achievers plan, track and achieve massive sales targets with clarity and focus."
    },
    {
      title: "How to Close High-Ticket Policies with Confidence",
      desc: "A step-by-step formula to present premium plans, handle objections, shift client perspective and close large-ticket policies with authority."
    }
  ];

  return (
    <div className="bg-white min-h-screen font-sans">
      {/* Hero Section */}
      <div className="bg-exiser-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10">
          <Link to="/categories" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 uppercase tracking-wider text-xs font-bold transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Programmes
          </Link>
          <div className="inline-flex items-center gap-2 bg-exiser-accent/20 border border-exiser-accent text-exiser-accent px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
            <Shield className="w-4 h-4" /> Corporate Services
          </div>
          <h1 className="text-4xl md:text-6xl font-bold uppercase leading-tight mb-6">
            Special Growth Mastery <br />
            <span className="text-exiser-accent">For Insurance Professionals</span>
          </h1>
          <p className="text-xl text-purple-100 max-w-3xl leading-relaxed mb-8">
            A premium breakthrough programme to transform your mindset, skills, communication and performance in the insurance industry.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/book" className="bg-exiser-accent text-exiser-primary px-8 py-4 font-bold uppercase tracking-wider rounded-sm hover:bg-white transition-all shadow-lg hover:shadow-xl hover:-translate-y-1">
              Book Consultation
            </Link>
            <a href="#syllabus" className="px-8 py-4 border border-white text-white font-bold uppercase tracking-wider rounded-sm hover:bg-white hover:text-exiser-primary transition-all">
              View Syllabus
            </a>
          </div>
        </div>
      </div>

      {/* Overview */}
      <div className="max-w-7xl mx-auto px-4 py-20">
        <div className="bg-purple-50 rounded-2xl p-8 md:p-12 border-l-8 border-exiser-primary shadow-lg">
          <h2 className="text-2xl font-bold uppercase text-gray-900 mb-4">Program Overview</h2>
          <p className="text-gray-700 text-lg leading-relaxed">
            This programme combines <span className="font-bold text-exiser-primary">growth psychology, neuroscience, practical sales tools, high-performance systems, and personal branding</span> — designed exclusively for insurance advisors, team leaders and MDRT aspirants who want exponential success.
          </p>
        </div>
      </div>

      {/* Syllabus Grid */}
      <div id="syllabus" className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold uppercase text-gray-900 mb-4">What Participants Will Master</h2>
            <div className="w-24 h-2 bg-exiser-accent mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {modules.map((mod, idx) => (
              <div key={idx} className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 group hover:-translate-y-2">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-exiser-primary mb-6 group-hover:bg-exiser-primary group-hover:text-white transition-colors">
                  <span className="font-bold text-xl">{idx + 1}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-exiser-primary transition-colors">{mod.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {mod.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Target Audience & Outcomes */}
      <div className="max-w-7xl mx-auto px-4 py-20">
        <div className="grid md:grid-cols-2 gap-16">

          {/* Who is this for */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Users className="w-8 h-8 text-exiser-primary" />
              <h3 className="text-3xl font-bold uppercase text-gray-900">Who This Is For</h3>
            </div>
            <ul className="space-y-4">
              {[
                "Insurance Advisors",
                "MDRT / COT / TOT Aspirants",
                "Agency Leaders",
                "Financial Planners",
                "Professionals wanting exponential growth"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="font-bold text-gray-800">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Outcomes */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-8 h-8 text-exiser-accent" />
              <h3 className="text-3xl font-bold uppercase text-gray-900">Powerful Outcomes</h3>
            </div>
            <div className="bg-gradient-to-br from-exiser-primary to-purple-900 rounded-2xl p-8 text-white shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
              <ul className="space-y-5 relative z-10">
                {[
                  "Remove biggest mental barriers",
                  "Gain confidence and clarity",
                  "Close high-ticket policies with ease",
                  "Build trust & influence authority",
                  "Master digital sales science",
                  "Develop high-performance habits",
                  "Fast-track MDRT/COT/TOT journey"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-exiser-accent flex-shrink-0 mt-1" />
                    <span className="font-sans text-purple-50">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </div>
      </div>

      {/* CTA */}
      <div className="bg-gray-900 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white uppercase mb-6">Ready To Dominate Your Market?</h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Spots for this specialized mastery programme are limited. Secure your consultation to see if you qualify.
          </p>
          <Link to="/book" className="inline-flex items-center gap-2 bg-exiser-accent text-exiser-primary px-10 py-5 font-bold uppercase tracking-wider rounded-sm hover:bg-white transition-colors">
            Apply Now <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SpecialGrowthMastery;